import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {BooleanState} from 'types';
import {
	Dialog,
	DialogFooter,
	DialogType,
	IconButton,
	IDialogContentProps,
	Stack,
} from '@fluentui/react';
import {BtnToCompareBaselines} from './BtnToCompareBaselines';

import {BaselinesPicker} from './BaselinesPicker/BaselinesPicker';
import {PossibleSelectedBaseline} from './BaselinesSelectionDialog.types';
import {useCommand} from 'hooks';
import {useBoolean} from '@fluentui/react-hooks';
import {useTranslationsOfBaselinesSelectionDialog} from './BaselinesSelectionDialog.utils';
import {ProjectDetailsPageParams} from 'features/VehicleProjects/vehicleProjectPagePaths';

interface Props {
	params: ProjectDetailsPageParams;
	baseline1Initial: PossibleSelectedBaseline | null;
}

export function BaselinesSelectionDialog({
	params,
	baseline1Initial,
}: Props): JSX.Element {
	const {t} = useTranslationsOfBaselinesSelectionDialog();

	const [baseline1, setBaseline1] = useState<PossibleSelectedBaseline | null>(
		null,
	);

	const [baseline2, setBaseline2] = useState<PossibleSelectedBaseline | null>(
		null,
	);

	useEffect(() => {
		setBaseline1(baseline1Initial);
	}, [baseline1Initial]);

	const infoForIsComparisonDialogOpen: BooleanState = useBoolean(false);

	const [isOpen, {setFalse, setTrue: showComparisonDialog}] =
		infoForIsComparisonDialogOpen;
	useCommand(
		{
			key: 'compare',
			text: t('ComparisonCmdTxt'),
			iconProps: {iconName: 'BranchCompare'},
			priority: 100,
			onClick: showComparisonDialog,
		},
		[showComparisonDialog],
	);

	const closeDialog = useCallback((): void => {
		setFalse();
		setBaseline1(null);
		setBaseline2(null);
	}, [setFalse]);

	const dialogContentProps = useMemo((): IDialogContentProps => {
		return {
			type: DialogType.close,
			title: t('Title'),
			closeButtonAriaLabel: t('LabelOfCloseBtn'),
		};
	}, [t]);

	return (
		<Dialog
			hidden={!isOpen}
			onDismiss={closeDialog}
			dialogContentProps={dialogContentProps}
			minWidth={700}
		>
			<Stack>
				<Stack.Item>
					<BaselinesPicker
						label={t('BaselineOld')}
						setBaseline={setBaseline1}
						possibleSelectedBaseline={baseline1}
					/>
				</Stack.Item>
				<Stack.Item align='center'>
					<IconButton
						iconProps={{iconName: 'SwitcherStartEnd'}}
						title='Switch'
						disabled={false}
						style={{marginTop: 30, padding: 20}}
						onClick={() => {
							setBaseline1(baseline2);
							setBaseline2(baseline1);
						}}
					/>
				</Stack.Item>
				<Stack.Item>
					<BaselinesPicker
						label={t('BaselineNew')}
						setBaseline={setBaseline2}
						possibleSelectedBaseline={baseline2}
					/>
				</Stack.Item>
			</Stack>
			<DialogFooter>
				<BtnToCompareBaselines
					params={params}
					baseline1Id={baseline1?.id ?? null}
					baseline2Id={baseline2?.id ?? null}
				/>
			</DialogFooter>
		</Dialog>
	);
}
